import { doc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import { IRecording } from '../../types/Recording';
import { getRecordingsByProjectKey } from '../recording/queries';

const RECORDINGS_COLLECTION = 'Recordings';
const FIRESTORE_BATCH_LIMIT = 500;

function buildResetCollectionLog(recording: IRecording) {
  const existing = recording.collectionLog;
  if (!existing) {
    return {
      timesCollected: 0,
      usersCollected: [],
      usersRemoved: [],
      usersNeverCollect: [],
    };
  }

  return {
    ...existing,
    timesCollected: 0,
  };
}

function recordingNeedsDownloadReset(recording: IRecording): boolean {
  return (recording.collectionLog?.timesCollected ?? 0) > 0;
}

export async function resetProjectDownloadCounts(projectId: string): Promise<number> {
  if (!projectId) {
    throw new Error('No project selected');
  }

  if (isDemoMode()) {
    const recordings = DemoDataManager.getRecordings(projectId);
    let resetCount = 0;

    for (const recording of recordings) {
      if (!recordingNeedsDownloadReset(recording)) continue;
      DemoDataManager.updateRecording(recording.key, {
        collectionLog: buildResetCollectionLog(recording),
      });
      resetCount += 1;
    }

    return resetCount;
  }

  const recordings = await getRecordingsByProjectKey(projectId);
  const recordingsToReset = recordings.filter(recordingNeedsDownloadReset);

  if (recordingsToReset.length === 0) {
    return 0;
  }

  let resetCount = 0;

  for (let index = 0; index < recordingsToReset.length; index += FIRESTORE_BATCH_LIMIT) {
    const chunk = recordingsToReset.slice(index, index + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);

    for (const recording of chunk) {
      batch.update(doc(db, RECORDINGS_COLLECTION, recording.key), {
        collectionLog: buildResetCollectionLog(recording),
      });
      resetCount += 1;
    }

    await batch.commit();
  }

  return resetCount;
}
